import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    useMediaQuery,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
    mediateEntriesInfoDependent,
    mediateLoginInfoDependent,
    mediateEntryDialogsLayoutInfoDependent,
    mediateSelectedListInfoDependent,
    mediateAddEntryRequester,
    mediateUpdateEntriesRequester,
    mediateUpdateListLimitsRequester,
} from 'mediators';
import { updateTasks } from 'mediators/requester/task-requester.mediator';
import { AddEntrySignal, UpdateEntriesSignal, UpdateListLimitsSignal, EVENT_KEYS } from 'presentation/events';
import { useStyles } from './cashflow.styles';
import { createDailyProjection, fmt, computeCreditOwedFromEntries } from './cashflow.utils';
import { getTodayCash } from 'core/services/tasks.service';
import { ProjectionTab } from './ProjectionTab';
import { EntriesTab } from './EntriesTab';
import { StatsTab } from './StatsTab';
import { SuggestionsPage } from './SuggestionsPage';
import { TemplatesPage } from './TemplatesPage';

const TAB_ROUTES = ['/projection', '/entries', '/stats', '/suggestions', '/templates'];

export const DailyCashflow: React.FC = () => {
    const classes  = useStyles();
    const dispatch = useDispatch();
    const history  = useHistory();
    const location = useLocation();

    const tab = location.pathname.startsWith('/entries')      ? 1
              : location.pathname.startsWith('/stats')        ? 2
              : location.pathname.startsWith('/suggestions')  ? 3
              : location.pathname.startsWith('/templates')    ? 4
              : 0;

    const isMobile = useMediaQuery('(max-width:600px)');

    const _entries: any[] = useSelector(mediateEntriesInfoDependent);
    const entries = useMemo(() => _entries || [], [_entries]);
    const login: any             = useSelector(mediateLoginInfoDependent);
    const entryDialogsLayout: any = useSelector(mediateEntryDialogsLayoutInfoDependent);
    const selectedList: any      = useSelector(mediateSelectedListInfoDependent);

    // ── Entry add/edit signals ──
    const [addEntrySignal]        = useState(new AddEntrySignal());
    const [updateEntriesSignal]   = useState(new UpdateEntriesSignal());
    const [updateListLimitsSignal] = useState(new UpdateListLimitsSignal());
    useEffect(() => mediateAddEntryRequester(addEntrySignal, dispatch).destroy,         [addEntrySignal, dispatch]);
    useEffect(() => mediateUpdateEntriesRequester(updateEntriesSignal, dispatch).destroy,   [updateEntriesSignal, dispatch]);
    useEffect(() => mediateUpdateListLimitsRequester(updateListLimitsSignal, dispatch).destroy, [updateListLimitsSignal, dispatch]);

    // Redirect old /inflows and /bills routes to /entries
    useEffect(() => {
        if (location.pathname.startsWith('/inflows') || location.pathname.startsWith('/bills')) {
            history.replace('/entries');
        }
    }, [location.pathname, history]);

    // ── Lifted filter state shared between ProjectionTab (row click) and EntriesTab ──
    const [entryDateFrom, setEntryDateFrom] = useState(() => sessionStorage.getItem('ef_from') || '');
    const [entryDateTo,   setEntryDateTo]   = useState(() => sessionStorage.getItem('ef_to')   || '');

    const setEntryDateFromP = useCallback((v: string) => {
        setEntryDateFrom(v);
        sessionStorage.setItem('ef_from', v);
    }, []);
    const setEntryDateToP = useCallback((v: string) => {
        setEntryDateTo(v);
        sessionStorage.setItem('ef_to', v);
    }, []);

    // ── Signal from Header "+" button (fires SHOW_ADD_ENTRY) ──
    // Incrementing counter so EntriesTab useEffect fires on every press (not just first).
    const [openAddFormSeq, setOpenAddFormSeq] = useState(0);
    useEffect(() => {
        if (entryDialogsLayout?.showAddEntry) {
            setOpenAddFormSeq(prev => prev + 1);
            if (tab === 0) history.push('/entries');
            dispatch({ type: EVENT_KEYS.SHOW_ADD_ENTRY, payload: { show: false } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryDialogsLayout?.showAddEntry]);

    // ── atRiskDates lifted so EntriesTab can highlight at-risk rows ──
    const [atRiskDates, setAtRiskDates] = useState<Set<string>>(new Set());

    // ── Today's cash balance — fetched from API, with client-side fallback ──
    const computeTodayCashLocal = useCallback(() => {
        const balance = selectedList?.startingBalance ?? 0;
        const entryCreditTotal = computeCreditOwedFromEntries(entries).total;
        const categoryLimits = selectedList?.categoryLimits || [];
        const projection = createDailyProjection(entries, balance, 0, null, categoryLimits);
        const todayRow = projection.find(r => r.isToday);
        const todayBalance = todayRow ? todayRow.balance : balance;
        const cashOnly = todayBalance - entryCreditTotal;
        return { balance: todayBalance, cash: cashOnly, creditOwed: entryCreditTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, selectedList]);

    const [todayCash, setTodayCash] = useState<{ balance: number; cash: number; creditOwed: number }>(
        { balance: 0, cash: 0, creditOwed: 0 }
    );

    useEffect(() => {
        const userId = login?.user?.id;
        if (!userId || !selectedList?.id) {
            setTodayCash(computeTodayCashLocal());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await getTodayCash(userId, selectedList.id);
                if (!cancelled && res && typeof res.balance === 'number') {
                    setTodayCash({
                        balance: Number(res.balance) || 0,
                        cash: Number(res.cash) || 0,
                        creditOwed: Number(res.creditOwed) || 0,
                    });
                } else if (!cancelled) {
                    setTodayCash(computeTodayCashLocal());
                }
            } catch {
                if (!cancelled) setTodayCash(computeTodayCashLocal());
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login?.user?.id, selectedList?.id, entries, selectedList]);

    // Re-fetch entries from server (after server-side mutations like duplicate/apply template)
    const handleEntriesChanged = useCallback(() => {
        if (selectedList) dispatch(updateTasks(selectedList) as any);
    }, [dispatch, selectedList]);

    // ── Callbacks for cross-tab navigation ──
    const navigateToEntries = useCallback(() => history.push('/entries'), [history]);

    // ProjectionTab calls this when user clicks "Credit Owed" amount — set onCredit filter
    // then navigate. EntriesTab reads sessionStorage on (re)mount to pick it up.
    const handleSetOnCreditFilter = useCallback((v: boolean) => {
        sessionStorage.setItem('ef_oncredit', String(v));
    }, []);

    // StatsTab calls these to pre-set Entries filters before navigating
    const setEntrySearch = useCallback((v: string) => {
        sessionStorage.setItem('ef_search', v);
    }, []);
    const setEntryTypeFilter = useCallback((v: 'all' | 'DEBIT' | 'CREDIT') => {
        sessionStorage.setItem('ef_type', v);
    }, []);
    const setEntryCategoryFilter = useCallback((v: string) => {
        sessionStorage.setItem('ef_cat', v);
    }, []);
    const setEntryTagFilter = useCallback((v: string) => {
        sessionStorage.setItem('ef_tag', v);
    }, []);

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Box className={classes.root} style={{ height: '100%' }}>
                {/* Global cash-today bar */}
                <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    padding: '6px 16px',
                    backgroundColor: todayCash.balance >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                }}>
                    <Typography style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                        Today
                    </Typography>
                    <Box style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: todayCash.balance >= 0 ? '#16a34a' : '#dc2626' }}>
                            {fmt(todayCash.balance)} cash
                        </Typography>
                        {todayCash.creditOwed > 0 && (
                            <Typography style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 600 }}>
                                {fmt(todayCash.creditOwed)} owed
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Tabs
                    value={tab}
                    onChange={(_, v) => history.push(TAB_ROUTES[v])}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Projection"               className={classes.tab} />
                    <Tab label={`Entries (${entries.length})`} className={classes.tab} />
                    <Tab label="Stats"                    className={classes.tab} />
                    <Tab label="AI"                       className={classes.tab} />
                    <Tab label="Templates"                className={classes.tab} />
                </Tabs>

                {tab === 0 && (
                    <ProjectionTab
                        entries={entries}
                        selectedList={selectedList}
                        login={login}
                        updateListLimitsSignal={updateListLimitsSignal}
                        isMobile={isMobile}
                        onNavigateToEntries={navigateToEntries}
                        onSetOnCreditFilter={handleSetOnCreditFilter}
                        onAtRiskDatesChange={setAtRiskDates}
                        setEntryDateFrom={setEntryDateFromP}
                        setEntryDateTo={setEntryDateToP}
                    />
                )}

                {tab === 1 && (
                    <EntriesTab
                        entries={entries}
                        addEntrySignal={addEntrySignal}
                        updateEntriesSignal={updateEntriesSignal}
                        atRiskDates={atRiskDates}
                        isMobile={isMobile}
                        onEntryEdit={(id) => history.push(`/entries/${id}`)}
                        entryDateFrom={entryDateFrom}
                        entryDateTo={entryDateTo}
                        setEntryDateFrom={setEntryDateFromP}
                        setEntryDateTo={setEntryDateToP}
                        openAddFormSeq={openAddFormSeq}
                        userId={login?.user?.id}
                        listId={selectedList?.id}
                        onEntriesChanged={handleEntriesChanged}
                    />
                )}

                {tab === 2 && (
                    <StatsTab
                        entries={entries}
                        selectedList={selectedList}
                        updateListLimitsSignal={updateListLimitsSignal}
                        isMobile={isMobile}
                        userId={login?.user?.id || ''}
                        onNavigateToEntries={navigateToEntries}
                        setEntrySearch={setEntrySearch}
                        setEntryTypeFilter={setEntryTypeFilter}
                        setEntryCategoryFilter={setEntryCategoryFilter}
                        setEntryTagFilter={setEntryTagFilter}
                        setEntryDateFrom={setEntryDateFromP}
                        setEntryDateTo={setEntryDateToP}
                    />
                )}

                {tab === 3 && (
                    <SuggestionsPage />
                )}

                {tab === 4 && (
                    <TemplatesPage />
                )}
            </Box>
        </MuiPickersUtilsProvider>
    );
};
